<script>
import Card from '@/rex-components/rex-card.vue';
import LogAddWidgetForm from '@/components/Log/LogAddWidgetForm.vue';
import {
  addwidgetMetricTypeOptions,
  addWidgetTableTypeOptions,
  getRouteName,
} from '@/components/Log/log.types';
import { CustomService } from '@/services/api/custom.service';
import { LOGService } from '@/services/api/log.service';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { notificationSucess } from '@/utils/notification';
import {
  getGraphConfig,
  getLabelConfig,
  getTableConfig,
} from '@/utils/custom/custom.log.widgets.config';
import {
  TABLE_QUERY_TYPE,
  LINE_GRAPH,
  BAR_GRAPH,
  STATUS_LABEL,
  TABLE_WIDGET,
} from '@/components/Log/log.constants';
import { CAPABILITY_LOG, CAPABILITY_ANALYTICS } from '@/utils/constants';

export default {
  components: { Card, LogAddWidgetForm },
  props: {
    queryType: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState('globalState/user', ['teamID']),
    ...mapState('globalState/app', ['currLang']),
    ...mapState('logState/logCommonState', ['isADType']),
    ...mapGetters('logState/logCommonState', [
      'getWidgetSavedSearchInfo',
      'getWidgetAddDashboardInfo',
      'getWidgetFormData',
      'getExecuteSavedSearch',
      'getCapabilityType',
    ]),
    getWidgetType() {
      return this.getWidgetAddDashboardInfo.widgetType;
    },
    getCapability() {
      return this.isADType ? CAPABILITY_ANALYTICS : CAPABILITY_LOG;
    },
  },
  data() {
    return {
      openForm: false,
      dashboardId: '',
      isLoading: false,
      widgets:
        this.queryType === TABLE_QUERY_TYPE
          ? addWidgetTableTypeOptions()
          : addwidgetMetricTypeOptions(),
      savedSearchRespData: {},
      isSavedSearchCreated: false,
      isNewDashCreated: false,
      isWidgetCreated: false,
      errMsg: '',
    };
  },
  methods: {
    ...mapMutations('logState/logCommonState', [
      'setAddDashboardInfoFields',
      'setSavedSearchInfoFields',
      'resetAddWidgetData',
      'setWidgetFormData',
    ]),
    clearFormData() {
      this.resetAddWidgetData('savedSearchInfo');
      this.resetAddWidgetData('addDashboardInfo');
      this.resetAddWidgetData('widgetInfo');
      this.resetAddWidgetData('widgetFormData');
    },
    resetErrData() {
      this.isSavedSearchCreated =
        !this.getWidgetFormData.isCreateOrUpdateSavedSearch;
      this.isNewDashCreated = !this.getWidgetFormData.isNewDash;
      this.isWidgetCreated = false;
      this.errMsg = '';
    },
    onWidgetClick(item) {
      this.clearFormData();
      this.setAddDashboardInfoFields({ field: 'widgetType', value: item.key });
      this.setSavedSearchInfoFields({
        field: 'query',
        value: this.$route.query.queryExec,
      });
      this.resetErrData();
      this.openForm = true;
    },
    updateSavedSearchRespInfo(isCreateOrUpdateSearch) {
      this.savedSearchRespData = !isCreateOrUpdateSearch
        ? {
            savedQueryName: this.getExecuteSavedSearch.savedSearchName,
            service_id: this.getExecuteSavedSearch.service_id,
            queryMetric: this.getExecuteSavedSearch.queryMetric,
          }
        : {};
    },
    async addSavedSearch() {
      try {
        if (!this.getWidgetFormData.isCreateOrUpdateSavedSearch) {
          this.updateSavedSearchRespInfo(false);
          return;
        }
        const searchData = {
          savedQueryName: this.getWidgetSavedSearchInfo.savedSearchName,
          query: this.getWidgetSavedSearchInfo.query,
          team_id: this.teamID,
          capability: this.getCapabilityType,
        };

        const { data } = await LOGService.addSavedSearchData(searchData);
        if (data.success) {
          this.savedSearchRespData = data.data;
          this.isSavedSearchCreated = true;
          if (
            this.$route.query.queryExec !== this.getWidgetSavedSearchInfo.query
          ) {
            this.$router.push({
              name: getRouteName(this.isADType),
              query: {
                ...this.$route.query,
                queryExec: this.getWidgetSavedSearchInfo.query || '',
              },
            });
          }
        } else {
          this.errMsg = this.$t('log.createSavedSearchErrMsg');
          this.isSavedSearchCreated = false;
          this.savedSearchRespData = {};
          throw new Error('Failed to add Saved search');
        }
      } catch (error) {
        console.log('error', error);
        this.savedSearchRespData = {};
        this.isSavedSearchCreated = false;
        this.errMsg =
          (error.status === 400 && error?.data?.errors?.message) ||
          this.$t('log.createSavedSearchErrMsg');
        throw new Error('Failed to add Saved search');
      }
    },
    async addDashboard() {
      try {
        const dashboardData = {
          name: this.getWidgetAddDashboardInfo.dashboardName,
          id: this.getWidgetAddDashboardInfo.dashboard?.id,
        };
        const copyDashId = this.getWidgetAddDashboardInfo.dashboard?.id;
        const { data } = !copyDashId
          ? await CustomService.addDashboard(dashboardData)
          : await CustomService.addDashboardFromId(dashboardData);

        if (data.success) {
          this.dashboardId = data.data[0].id;
          this.isNewDashCreated = true;
        } else {
          this.isNewDashCreated = false;
          this.errMsg = this.$t('errorAddingDashboard');
          throw new Error('Failed to add new dashboard');
        }
      } catch (error) {
        console.log('error', error);
        this.isNewDashCreated = false;
        this.errMsg =
          (error.status === 400 && error?.data?.message) ||
          this.$t('errorAddingDashboard');
        throw new Error('Failed to add new dashboard');
      }
    },
    getWidgetReqData() {
      if (
        this.getWidgetType === LINE_GRAPH ||
        this.getWidgetType === BAR_GRAPH
      ) {
        return getGraphConfig({
          widgetName: this.getWidgetAddDashboardInfo.widgetName,
          metricName: this.savedSearchRespData.savedQueryName,
          serviceKey: this.savedSearchRespData.service_id,
          xLabel: 'Time',
          yLabel: 'Count',
          savedSearchName: this.savedSearchRespData.savedQueryName,
          chartType: this.getWidgetAddDashboardInfo.widgetType,
          capability: this.getCapability,
          queryMetric: this.savedSearchRespData.queryMetric,
        });
      }

      if (this.getWidgetType === STATUS_LABEL) {
        return getLabelConfig({
          widgetName: this.getWidgetAddDashboardInfo.widgetName,
          savedSearchName: this.savedSearchRespData.savedQueryName,
          serviceKey: this.savedSearchRespData.service_id,
          capability: this.getCapability,
          metricName: this.savedSearchRespData.queryMetric,
        });
      }

      if (this.getWidgetType === TABLE_WIDGET) {
        return getTableConfig({
          widgetName: this.getWidgetAddDashboardInfo.widgetName,
          savedSearchName: this.savedSearchRespData.savedQueryName,
          serviceKey: this.savedSearchRespData.service_id,
          capability: this.getCapability,
        });
      }
    },
    async addWidget() {
      try {
        const widget = this.getWidgetReqData();
        const dashWidgetData = {
          id: this.getWidgetFormData.isNewDash
            ? this.dashboardId
            : this.getWidgetAddDashboardInfo.dashboard.id,
          widget,
        };
        const { data } =
          await CustomService.postDashboardTemplate(dashWidgetData);

        if (data.success) {
          this.isWidgetCreated = true;
        } else {
          this.isWidgetCreated = false;
          this.errMsg = this.$t('errorAddingWidget');
          throw new Error('Failed to add widget');
        }
      } catch (error) {
        console.log('error', error);
        this.isWidgetCreated = false;
        this.errMsg =
          (error.status === 400 && error?.data?.message) ||
          this.$t('errorAddingWidget');
        throw new Error('Failed to add widget');
      }
    },
    async onSubmit() {
      try {
        this.errMsg = '';
        this.isLoading = true;
        if (!this.isSavedSearchCreated) {
          await this.addSavedSearch();
        }
        if (this.getWidgetFormData.isNewDash && !this.isNewDashCreated) {
          await this.addDashboard();
        }
        if (!this.isWidgetCreated) {
          await this.addWidget();
        }
        if (
          ((this.getWidgetFormData.isCreateOrUpdateSavedSearch &&
            this.isSavedSearchCreated) ||
            !this.getWidgetFormData.isCreateOrUpdateSavedSearch) &&
          ((this.getWidgetFormData.isNewDash && this.isNewDashCreated) ||
            !this.getWidgetFormData.isNewDash) &&
          this.isWidgetCreated
        ) {
          this.openForm = false;
          notificationSucess(this.$t('log.createWidgetSuccMsg'));
        }
        this.isLoading = false;
      } catch (error) {
        console.log('error', error);
        this.isLoading = false;
      }
    },
    mouseAction(index, entered) {
      const tempWidgets = [...this.widgets];
      tempWidgets[index].dispGif = entered;
      this.widgets = tempWidgets;
    },
  },
  watch: {
    queryType(newVal) {
      this.widgets =
        newVal === TABLE_QUERY_TYPE
          ? addWidgetTableTypeOptions()
          : addwidgetMetricTypeOptions();
    },
    currLang() {
      this.widgets =
        this.queryType === TABLE_QUERY_TYPE
          ? addWidgetTableTypeOptions()
          : addwidgetMetricTypeOptions();
    },
    'getWidgetFormData.isNewDash'(newVal) {
      this.isNewDashCreated = !newVal;
    },
    'getWidgetFormData.isCreateOrUpdateSavedSearch': {
      immediate: true,
      handler(newVal) {
        this.isSavedSearchCreated = !newVal;
        this.updateSavedSearchRespInfo(newVal);
      },
    },
  },
};
</script>

<template>
  <div>
    <Card :header="$t('log.addWidget')">
      <template #card-body>
        <div>
          <div>
            <button
              v-for="(item, i) in widgets"
              :key="i"
              class="widget-btn"
              :id="`log-${item.key}-widget-btn`"
              @click="() => onWidgetClick(item)"
              @mouseenter="() => mouseAction(i, true)"
              @mouseleave="() => mouseAction(i, false)"
            >
              <div class="add-widget-img-header">
                {{ item.label }}
              </div>
              <div class="add-widget-img-box">
                <BaseIcon
                  v-show="!item.dispGif"
                  height="6.375rem"
                  width="15.625rem"
                  :name="item.iconName"
                />
                <img
                  v-show="item.dispGif"
                  :src="require(`@/assets/gifs/${item.iconName}.gif`)"
                  :alt="item.label"
                  :style="{
                    height: '6.375rem',
                    width: '15.625rem',
                  }"
                />
              </div>
            </button>
          </div>
          <LogAddWidgetForm
            v-if="openForm"
            @onClose="openForm = false"
            @onSubmit="onSubmit"
            :isLoading="isLoading"
            :formErrMsg="errMsg"
          />
        </div>
      </template>
    </Card>
  </div>
</template>

<style lang="scss" scoped>
.widget-btn {
  margin-bottom: 1rem;
  padding: 0rem;
  border: 0.063rem solid $bg-tertiary;
  border-radius: 0.5rem;
  cursor: pointer;
  background-color: $bg-secondary;
}
.widget-btn:hover {
  border-color: $color-brand-accent;
}
.add-widget-img-header {
  @include flex-center-justify;
  padding-top: 2rem;
  color: $font-primary;
  font-size: 0.875rem;
  font-weight: 600;
}
.add-widget-img-box {
  padding: 0.625rem 1rem 2rem 1rem;
}

:deep(.card),
.crd {
  border-radius: 0rem;
  border: none;
  border-radius: 0.7875rem;
}
:deep(.card-header) {
  @include flex-center;
  border-bottom: none;
  background-color: $bg-tertiary;
  border-radius: 0rem;
  border-radius: 0.7875rem 0.7875rem 0rem 0rem !important;
  height: 3.5rem;
  font-size: 1.125rem;
}
</style>
